import { inject, Service } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { UserCredentials, UserProfile } from '@models/user';

const STORAGE_KEYS = {
  USER_CREDENTIALS: 'user_credentials',
  USER_PROFILE: 'user_profile',
  REMEMBERED_EMAIL: 'remembered_email',
} as const;

@Service()
export class StorageService {
  private readonly storage = inject(Storage);
  private initPromise: Promise<Storage> | null = null;

  private ensureReady(): Promise<Storage> {
    this.initPromise ??= this.storage.create();
    return this.initPromise;
  }

  async setUserCredentials(credentials: UserCredentials): Promise<void> {
    await this.ensureReady();
    await this.storage.set(STORAGE_KEYS.USER_CREDENTIALS, credentials);
  }

  async getUserCredentials(): Promise<UserCredentials | null> {
    await this.ensureReady();
    return (await this.storage.get(STORAGE_KEYS.USER_CREDENTIALS)) as UserCredentials | null;
  }

  async removeUserCredentials(): Promise<void> {
    await this.ensureReady();
    await this.storage.remove(STORAGE_KEYS.USER_CREDENTIALS);
  }

  async setCurrentUser(user: UserProfile): Promise<void> {
    await this.ensureReady();
    await this.storage.set(STORAGE_KEYS.USER_PROFILE, user);
  }

  async getCurrentUser(): Promise<UserProfile | null> {
    await this.ensureReady();
    return (await this.storage.get(STORAGE_KEYS.USER_PROFILE)) as UserProfile | null;
  }

  async removeCurrentUser(): Promise<void> {
    await this.ensureReady();
    await this.storage.remove(STORAGE_KEYS.USER_PROFILE);
  }

  async setRememberedEmail(email: string): Promise<void> {
    await this.ensureReady();
    await this.storage.set(STORAGE_KEYS.REMEMBERED_EMAIL, email);
  }

  async getRememberedEmail(): Promise<string | null> {
    await this.ensureReady();
    return (await this.storage.get(STORAGE_KEYS.REMEMBERED_EMAIL)) as string | null;
  }
}
